import { buttonClassName } from '@emailforge/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '@/components/ui/icons';
import { type PricingResponse,useGetPricingQuery } from '@/features/billing/billingApi';

type BillingPeriod = 'monthly' | 'annual';

/**
 * Stripe returns minor units (€29.00 → 2900). All our prices are whole-euro
 * so we round to integers; if we ever introduce sub-euro pricing this needs
 * to use full Intl formatting.
 */
function formatEur(minor: number): string {
  return `€${Math.round(minor / 100)}`;
}

/**
 * Pricing page — v2 design adapted. Three sections:
 *  1. 4-plan card grid with billing toggle
 *  2. Enterprise band with capability chips
 *  3. Full feature-comparison matrix (6 categories) + customer logo strip
 *
 * Copy retains EmailOps positioning (no "Git for emails").
 */
export function PricingPage() {
  const [billing, setBilling] = useState<BillingPeriod>('annual');
  return (
    <>
      <Hero billing={billing} onChange={setBilling} />
      <PlanCards billing={billing} />
      <EnterpriseBand />
      <ComparisonMatrix />
      <CustomersStrip />
      <FinalCta />
    </>
  );
}

function Hero({ billing, onChange }: { billing: BillingPeriod; onChange: (b: BillingPeriod) => void }) {
  return (
    <section style={{ textAlign: 'center', padding: '72px 40px 24px' }}>
      <div
        className="mono"
        style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.08 }}
      >
        Pricing
      </div>
      <h1
        className="display"
        style={{ fontSize: 64, letterSpacing: -0.025, lineHeight: 1.04, marginTop: 10 }}
      >
        Straightforward pricing, <i style={{ color: 'var(--flame)' }}>no surprises</i>.
      </h1>
      <p
        style={{
          fontSize: 16,
          color: 'var(--ink-2)',
          marginTop: 14,
          maxWidth: 640,
          margin: '14px auto 0',
        }}
      >
        Free forever option, one-time pack for quick projects, subscriptions for teams that ship.
      </p>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 24 }}
      >
        <div
          role="tablist"
          aria-label="Billing period"
          style={{
            display: 'inline-flex',
            border: '1px solid var(--line)',
            borderRadius: 999,
            padding: 3,
            background: 'var(--paper-2)',
          }}
        >
          <BillingTab active={billing === 'monthly'} label="Monthly" onClick={() => onChange('monthly')} />
          <BillingTab active={billing === 'annual'} label="Annual" onClick={() => onChange('annual')} />
        </div>
        <span className="mono" style={{ fontSize: 11, color: 'var(--flame)', fontWeight: 600 }}>
          {billing === 'annual' ? 'Save 2 months' : '−17% on annual'}
        </span>
      </div>
    </section>
  );
}

function BillingTab({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: '5px 16px',
        fontSize: 12.5,
        background: active ? 'var(--flame)' : 'transparent',
        color: active ? 'white' : 'var(--ink-3)',
        borderRadius: 999,
        fontWeight: active ? 550 : 400,
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}

type Pricing =
  | { kind: 'free' }
  | { kind: 'oneTime'; fallback: number; apiKey: 'exportPack' }
  | {
      kind: 'subscription';
      // Fallbacks (in EUR, whole units) used while /api/pricing is loading or
      // if Stripe is unreachable. Resolved live values from Stripe override these.
      fallbackMonthly: number;
      fallbackAnnual: number;
      apiKey: 'starter' | 'pro';
    };

type Plan = {
  name: string;
  eye: string;
  pricing: Pricing;
  features: string[];
  cta: string;
  to: string;
  flame?: boolean;
  chip?: string;
  note?: string;
};

const PLANS: Plan[] = [
  {
    name: 'Free',
    eye: 'For exploring',
    pricing: { kind: 'free' },
    features: ['3 templates', 'Gmail + Apple preview', 'Export to .tsx & HTML', 'Community support'],
    cta: 'Start free',
    to: '/signup',
    note: '"Built with Emailforge" watermark',
  },
  {
    name: 'Export Pack',
    eye: 'For one-off projects',
    pricing: { kind: 'oneTime', fallback: 29, apiKey: 'exportPack' },
    features: ['10 templates total', '90 days access', 'Unlimited export', 'Full library', 'No watermark'],
    cta: 'Get Export Pack',
    to: '/checkout/export-pack',
    note: 'No sending, no API',
  },
  {
    name: 'Starter',
    eye: 'For shipping apps',
    pricing: { kind: 'subscription', fallbackMonthly: 29, fallbackAnnual: 290, apiKey: 'starter' },
    features: [
      '10 published templates',
      '1 sender verified',
      '1,000 emails/mo',
      '3 triggers',
      'Versioning (last 10)',
      '14-day free trial',
    ],
    cta: 'Start Starter',
    to: '/checkout/starter',
    flame: true,
    chip: 'Most popular',
  },
  {
    name: 'Pro',
    eye: 'For growing teams',
    pricing: { kind: 'subscription', fallbackMonthly: 79, fallbackAnnual: 790, apiKey: 'pro' },
    features: [
      'Unlimited templates',
      '5 senders',
      '10,000 emails/mo',
      'Unlimited triggers',
      'A/B testing',
      'Custom tracking domain',
      'Team + approval flow',
    ],
    cta: 'Start Pro',
    to: '/checkout/pro',
  },
];

function resolvePrice(
  p: Plan,
  billing: BillingPeriod,
  data: PricingResponse | undefined,
): { price: string; oldPrice?: string; unit: string } {
  if (p.pricing.kind === 'free') {
    return { price: '€0', unit: 'forever' };
  }
  if (p.pricing.kind === 'oneTime') {
    const live = data?.exportPack.once;
    return {
      price: live ? formatEur(live.amount) : `€${p.pricing.fallback}`,
      unit: 'once · 90 days',
    };
  }
  // subscription
  const live = data?.[p.pricing.apiKey];
  if (billing === 'annual') {
    const annualMinor = live?.yearly?.amount ?? p.pricing.fallbackAnnual * 100;
    const monthlyMinor = live?.monthly?.amount ?? p.pricing.fallbackMonthly * 100;
    return {
      price: formatEur(annualMinor),
      oldPrice: formatEur(monthlyMinor * 12),
      unit: 'per year',
    };
  }
  const monthlyMinor = live?.monthly?.amount ?? p.pricing.fallbackMonthly * 100;
  return {
    price: formatEur(monthlyMinor),
    unit: 'per month',
  };
}

function PlanCards({ billing }: { billing: BillingPeriod }) {
  // Live prices from Stripe; falls through to plan-level fallbacks while loading
  // or on error. Cache is server-side (5 min); RTK Query also caches per session.
  const { data: pricing } = useGetPricingQuery();
  return (
    <section
      style={{
        padding: '12px 40px 48px',
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 14,
        maxWidth: 1280,
        margin: '0 auto',
      }}
    >
      {PLANS.map((p) => {
        const { price, oldPrice, unit } = resolvePrice(p, billing, pricing);
        return (
        <div
          key={p.name}
          style={{
            position: 'relative',
            background: 'var(--paper)',
            border: p.flame ? '1.5px solid var(--flame)' : '1px solid var(--line)',
            borderRadius: 10,
            padding: '26px 22px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {p.chip && (
            <div
              style={{
                position: 'absolute',
                top: -11,
                left: 22,
                background: 'var(--flame)',
                color: 'white',
                padding: '3px 10px',
                fontSize: 10.5,
                borderRadius: 999,
                fontWeight: 550,
              }}
            >
              {p.chip}
            </div>
          )}
          <div
            className="mono"
            style={{
              fontSize: 10.5,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
            }}
          >
            {p.eye}
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>{p.name}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 16 }}>
            <div className="display" style={{ fontSize: 48, letterSpacing: -0.02, lineHeight: 1 }}>
              {price}
            </div>
            {oldPrice && (
              <div
                className="mono"
                style={{ fontSize: 12, color: 'var(--ink-3)', textDecoration: 'line-through' }}
              >
                {oldPrice}
              </div>
            )}
          </div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
            {unit}
          </div>
          <div style={{ height: 1, background: 'var(--line-soft)', margin: '18px 0' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
            {p.features.map((f) => (
              <div key={f} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                <span style={{ color: 'oklch(0.55 0.14 148)', marginTop: 1 }}>
                  <Icon.check size={13} />
                </span>
                <span>{f}</span>
              </div>
            ))}
          </div>
          {p.note && (
            <div
              className="mono"
              style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 10, fontStyle: 'italic' }}
            >
              {p.note}
            </div>
          )}
          <Link
            to={p.to}
            className={buttonClassName({ variant: p.flame ? 'flame' : 'default' })}
            style={{ height: 36, marginTop: 20, justifyContent: 'center', width: '100%' }}
          >
            {p.cta}
          </Link>
        </div>
        );
      })}
    </section>
  );
}

function EnterpriseBand() {
  const chips = [
    'SAML SSO',
    'SCIM',
    'EU data residency',
    'Audit log',
    'Custom DPA',
    '99.99% SLA',
  ];
  return (
    <section
      style={{
        margin: '0 40px 48px',
        padding: '24px 28px',
        background: 'var(--paper-2)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 28,
        maxWidth: 1200,
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <div className="display" style={{ fontSize: 28, letterSpacing: -0.015, maxWidth: 360, lineHeight: 1.15 }}>
        Volume, SSO, or data residency?
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1 }}>
        {chips.map((c) => (
          <span
            key={c}
            className="mono"
            style={{
              fontSize: 11,
              padding: '4px 10px',
              background: 'var(--paper)',
              border: '1px solid var(--line)',
              borderRadius: 999,
            }}
          >
            {c}
          </span>
        ))}
      </div>
      <Link
        to="/contact"
        style={{
          color: 'var(--flame)',
          fontSize: 14,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          textDecoration: 'none',
        }}
      >
        Talk to sales <Icon.arrow size={13} />
      </Link>
    </section>
  );
}

type ComparisonRow = [string, string, string, string, string]; // feature, free, export, starter, pro

const COMPARISON_CATEGORIES: { cat: string; rows: ComparisonRow[] }[] = [
  {
    cat: 'Templates',
    rows: [
      ['Max templates', '3', '10', '10', 'Unlimited'],
      ['Drafts', 'Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'],
      ['Fork from gallery', '✓', '✓', '✓', '✓'],
      ['Collections', '—', '—', '✓', '✓'],
    ],
  },
  {
    cat: 'Editor',
    rows: [
      ['Visual editor', '✓', '✓', '✓', '✓'],
      ['Code view (.tsx)', '✓', '✓', '✓', '✓'],
      ['Design tokens', '—', '✓', '✓', '✓'],
      ['Dark mode preview', '—', '—', '✓', '✓'],
    ],
  },
  {
    cat: 'Sending',
    rows: [
      ['Senders', '—', '—', '1', '5'],
      ['Emails/mo via API', '—', '—', '1,000', '10,000'],
      ['Triggers', '—', '—', '3', 'Unlimited'],
      ['A/B testing', '—', '—', '—', '✓'],
    ],
  },
  {
    cat: 'Versioning',
    rows: [
      ['History depth', '—', 'Current', 'Last 10', 'Unlimited'],
      ['Rollback', '—', '—', '✓', '✓'],
      ['Approval flow', '—', '—', '—', '✓'],
    ],
  },
  {
    cat: 'Collaboration',
    rows: [
      ['Team members', '1', '1', '5', 'Unlimited'],
      ['Comments', '—', '—', '✓', '✓'],
      ['Mentions', '—', '—', '✓', '✓'],
    ],
  },
  {
    cat: 'Compliance',
    rows: [
      ['GDPR', '✓', '✓', '✓', '✓'],
      ['DPA', '—', '—', '✓', '✓'],
      ['EU data residency', '—', '—', '—', 'Enterprise'],
      ['SAML SSO', '—', '—', '—', 'Enterprise'],
    ],
  },
];

function ComparisonMatrix() {
  return (
    <section
      style={{
        margin: '0 40px',
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        overflow: 'hidden',
        maxWidth: 1280,
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--line)' }}>
        <div
          className="mono"
          style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.08, color: 'var(--ink-3)' }}
        >
          Full comparison
        </div>
        <div className="display" style={{ fontSize: 24, letterSpacing: -0.015, marginTop: 2 }}>
          Every feature, every plan.
        </div>
      </div>
      <table className="mf-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ minWidth: 260, textAlign: 'left' }}>Feature</th>
            <th style={{ textAlign: 'center' }}>Free</th>
            <th style={{ textAlign: 'center' }}>Export Pack</th>
            <th style={{ textAlign: 'center' }}>Starter</th>
            <th
              style={{
                textAlign: 'center',
                background: 'var(--flame-soft)',
                color: 'var(--flame-2)',
                fontWeight: 700,
              }}
            >
              Pro · recommended
            </th>
          </tr>
        </thead>
        <tbody>
          {COMPARISON_CATEGORIES.map((cat) => (
            <ComparisonGroup key={cat.cat} cat={cat.cat} rows={cat.rows} />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ComparisonGroup({ cat, rows }: { cat: string; rows: ComparisonRow[] }) {
  return (
    <>
      <tr>
        <td
          colSpan={5}
          style={{
            background: 'var(--paper-2)',
            padding: '8px 12px',
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            textTransform: 'uppercase',
            letterSpacing: 0.08,
            color: 'var(--ink-3)',
          }}
        >
          {cat}
        </td>
      </tr>
      {rows.map(([label, free, exp, starter, pro]) => (
        <tr key={label}>
          <td style={{ fontSize: 13 }}>{label}</td>
          <ComparisonCell value={free} highlighted={false} />
          <ComparisonCell value={exp} highlighted={false} />
          <ComparisonCell value={starter} highlighted={false} />
          <ComparisonCell value={pro} highlighted={true} />
        </tr>
      ))}
    </>
  );
}

function ComparisonCell({ value, highlighted }: { value: string; highlighted: boolean }) {
  const isCheck = value === '✓';
  const isDash = value === '—';
  const isMonoLooking = !isCheck && !isDash && (value.includes('/') || /^\d/.test(value));
  return (
    <td
      style={{
        textAlign: 'center',
        fontSize: 12.5,
        fontFamily: isMonoLooking ? 'var(--font-mono)' : 'var(--font-sans)',
        background: highlighted ? 'var(--flame-soft)' : 'transparent',
      }}
    >
      {isCheck ? (
        <span style={{ color: 'oklch(0.55 0.14 148)' }}>
          <Icon.check size={13} />
        </span>
      ) : isDash ? (
        <span style={{ color: 'var(--ink-4, var(--ink-3))' }}>—</span>
      ) : (
        value
      )}
    </td>
  );
}

function CustomersStrip() {
  const logos = [
    'RELAY',
    'FREQUENCY',
    'KILOBYTE',
    'ACME INC',
    'BASEPLATE',
    'MERIDIAN',
    'NORTHSTAR',
    'HEARTH',
    'FIELDNOTES',
    'SOLACE',
  ];
  return (
    <section style={{ padding: '44px 40px' }}>
      <div
        className="mono"
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.08,
          textAlign: 'center',
        }}
      >
        Teams building with Emailforge
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 36,
          marginTop: 16,
          flexWrap: 'wrap',
        }}
      >
        {logos.map((l) => (
          <span
            key={l}
            className="mono"
            style={{ fontSize: 13, color: 'var(--ink-3)', letterSpacing: 0.12, fontWeight: 500 }}
          >
            {l}
          </span>
        ))}
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      style={{
        padding: '56px 40px',
        textAlign: 'center',
        background: 'var(--paper-2)',
        borderTop: '1px solid var(--line)',
      }}
    >
      <div className="display" style={{ fontSize: 32, letterSpacing: -0.015 }}>
        Still not sure?
      </div>
      <div style={{ fontSize: 15, color: 'var(--ink-2)', marginTop: 8 }}>
        Start with the free plan. No card, no catch.
      </div>
      <Link
        to="/signup"
        style={{
          color: 'var(--flame)',
          fontSize: 14,
          fontWeight: 600,
          marginTop: 14,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          textDecoration: 'none',
        }}
      >
        Start free →
      </Link>
    </section>
  );
}
